import { type ChangeEvent, useState, type ReactNode  } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Stack, type SxProps, Theme } from '@mui/material';

import { partnerClientCreateExtended } from '@/entities/partner';
import { Dialog } from '@/shared/ui/Dialog';
import { Button } from '@/shared/ui/lib/Button';
import { TextField } from '@/shared/ui/lib/TextField';
import { CustomTypography } from '@/shared/ui/Typography';

interface AddClientDialogProps {
  open: boolean;
  onClose: () => void;
}

const helperSx: SxProps<Theme> = { color: 'text.secondary' };

const phoneLabelFallback = (key: string, value: string) =>
  value && value !== key ? value : 'Телефон';

export function AddClientDialog({ open, onClose }: AddClientDialogProps): ReactNode {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');

  const create = useMutation({
    ...partnerClientCreateExtended(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['partner', 'clients-extended'] });
      qc.invalidateQueries({ queryKey: ['partner', 'clients-stats'] });
      setEmail('');
      setPhone('');
      setName('');
      onClose();
    },
  });

  const isValid =
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) &&
    /^\+\d{7,15}$/.test(phone.trim()) &&
    name.trim().length > 0;

  const phoneLabelKey = 'common:affiliatePage.dialogs.addClient.phoneLabel';
  const phoneLabel = phoneLabelFallback(phoneLabelKey, t(phoneLabelKey));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('common:affiliatePage.dialogs.addClient.title') ?? ''}
      leftButtonSlot={
        <Button onClick={onClose} disabled={create.isPending}>
          {t('common:buttons.cancel')}
        </Button>
      }
      rightButtonSlot={
        <Button
          variant="contained"
          disabled={!isValid || create.isPending}
          onClick={() =>
            create.mutate({
              email: email.trim(),
              phone: phone.trim(),
              name: name.trim(),
            })
          }
        >
          {t('common:buttons.add')}
        </Button>
      }
    >
      <Stack spacing={2} sx={{ minWidth: 360 }}>
        <CustomTypography variant="caption" sx={helperSx}>
          {t('common:affiliatePage.dialogs.addClient.helper')}
        </CustomTypography>
        <TextField
          label={t('common:affiliatePage.dialogs.addClient.emailLabel')}
          placeholder="E-mail"
          value={email}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
          fullWidth
        />
        <TextField
          label={phoneLabel}
          placeholder="+7XXXXXXXXXX"
          value={phone}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setPhone(e.target.value)}
          fullWidth
        />
        <TextField
          label={t('common:affiliatePage.dialogs.addClient.nameLabel')}
          placeholder={t('common:affiliatePage.dialogs.addClient.namePlaceholder') ?? ''}
          value={name}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
          fullWidth
        />
      </Stack>
    </Dialog>
  );
}
